// ============================================================================
// BehaviorRepeatIncidents.tsx
// Section component for displaying repeat behavior incidents by resident
// Similar to Fall Analysis Repeat Fall Incidents section
// ============================================================================

import { Bar } from "react-chartjs-2";
import { Fragment, useEffect, useState } from "react";
import { Container, Child, Header, LayerContent } from "../styles";
import { ChartContainer, NoData } from "../../style";
import { useAnalyticsWrapper } from "../../../../../store/store";
import {
  barOptionsHorizontal,
  handlePointClicked,
} from "../formUtils";
import SectionModal from "./BehaviorAnalysisSectionModal";
import {
  handleCSVDownload,
  handlePDFDownload,
} from "../../../../../utils/helpers/pdfCsvUtils";
import { behaviorAnalysisDetailCols } from "../../dummyData/colums";
import { Grid, GridColumn, GridColumnProps } from "@progress/kendo-react-grid";
import {
  CenteredWholeNumCell,
  HeaderCenteredCell,
} from "../../../../../components/DynamicGrid/customCells";
import { TotalRow } from "../../../../../components/DynamicGrid/customRows";
import useFormDataHelpers from "../../../../../hooks/useFormDataHelpers";
import { MITIGATE_COLORS } from "../../../../../shared/sharedConstants";

// ============================================================================
// Props Interface
// ============================================================================
interface BehaviorRepeatIncidentsProps {
  setClickedIncident: (item: any) => void;
  isFetchingSelectedIncident: boolean;
  shareTableData?: (data: any[]) => void;
}

// ============================================================================
// Row Type Definition
// ============================================================================
type RepeatRow = {
  residentName: string;
  count: number;
  percentage: number;
};

// ============================================================================
// Component
// ============================================================================
const BehaviorRepeatIncidents = ({
  setClickedIncident,
  isFetchingSelectedIncident,
  shareTableData,
}: BehaviorRepeatIncidentsProps) => {
  // ---------------------------------------------------------------------------
  // Helper Hooks
  // ---------------------------------------------------------------------------
  const { getFacilityLabel } = useFormDataHelpers();

  // ---------------------------------------------------------------------------
  // Local State
  // ---------------------------------------------------------------------------
  const [tableData, setTableData] = useState<RepeatRow[]>([]);
  const [grid, setGrid] = useState<any>([]);
  const [barData, setBarData] = useState<any>([]);
  const [modalData, setModalData] = useState<any>([]);
  const [residentName, setResidentName] = useState("");
  const [showModal, setShowModal] = useState(false);

  // ---------------------------------------------------------------------------
  // Global State from Zustand
  // ---------------------------------------------------------------------------
  const { startDate, endDate, behaviorFacility, behaviorAnalysisIncidents } =
    useAnalyticsWrapper((state) => ({
      startDate: state.startDate,
      endDate: state.endDate,
      behaviorFacility: state.behaviorFacility,
      behaviorAnalysisIncidents: state.behaviorAnalysisIncidents,
    }));

  // Derived value for facility name
  const facilityLabel = getFacilityLabel(behaviorFacility);

  // ---------------------------------------------------------------------------
  // Data Transformation Function - converts drilldown object to table rows
  // ---------------------------------------------------------------------------
  const adjustedTableData = (drilldownData: Record<string, any[]>): RepeatRow[] => {
    if (!drilldownData || typeof drilldownData !== 'object') {
      return [];
    }

    let totalIncidentsSum = 0;
    const rows: RepeatRow[] = [];

    // Process each resident and count their incidents
    Object.entries(drilldownData).forEach(([residentName, incidents]) => {
      const count = Array.isArray(incidents) ? incidents.length : 0;
      totalIncidentsSum += count;
      rows.push({
        residentName,
        count,
        percentage: 0, // Will calculate after we have total
      });
    });

    // Sort by count descending (most incidents first)
    rows.sort((a, b) => b.count - a.count);

    // Calculate percentages
    rows.forEach((row) => {
      row.percentage = totalIncidentsSum > 0 
        ? Math.round((row.count / totalIncidentsSum) * 100) 
        : 0;
    });

    // Add totals row
    rows.push({
      residentName: "Total",
      count: totalIncidentsSum,
      percentage: 100,
    });

    return rows;
  };

  // ---------------------------------------------------------------------------
  // Transform table data for horizontal bar chart (ensures chart order matches table order)
  // ---------------------------------------------------------------------------
  const transformTableDataForChart = (rows: RepeatRow[]) => {
    // Filter out the totals row for the chart
    const dataRows = rows.filter(row => row.residentName !== "Total");
    
    if (dataRows.length === 0) {
      return { labels: [], datasets: [] };
    }

    const labels = dataRows.map(row => row.residentName);
    const data = dataRows.map(row => row.count);

    // Alternating colors from MITIGATE_COLORS
    const alternatingColors = [
      MITIGATE_COLORS.BLUE[0],
      MITIGATE_COLORS.GREEN[0],
      MITIGATE_COLORS.RED[0],
      MITIGATE_COLORS.ORANGE[0],
      MITIGATE_COLORS.PURPLE[0],
      MITIGATE_COLORS.BLUE[1],
      MITIGATE_COLORS.GREEN[1],
      MITIGATE_COLORS.RED[1],
      MITIGATE_COLORS.ORANGE[1],
      MITIGATE_COLORS.PURPLE[1],
      MITIGATE_COLORS.BLUE[2],
      MITIGATE_COLORS.GREEN[2],
      MITIGATE_COLORS.RED[2],
      MITIGATE_COLORS.ORANGE[2],
      MITIGATE_COLORS.PURPLE[2],
    ];

    return {
      labels,
      datasets: [
        {
          label: "# of Incidents",
          data,
          backgroundColor: alternatingColors.slice(0, labels.length),
          borderWidth: 0,
        },
      ],
    };
  };

  // ---------------------------------------------------------------------------
  // Process data when behaviorAnalysisIncidents changes
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (behaviorAnalysisIncidents !== undefined) {
      const repeatSection = behaviorAnalysisIncidents?.repeatIncidentsSection;
      // Use repeatIncidentsDrilldown - object keyed by resident name with arrays of incidents
      const drilldownData = repeatSection?.repeatIncidentsDrilldown || {};
      
      const transformedData = adjustedTableData(drilldownData);
      const transformedBarData = transformTableDataForChart(transformedData);

      setTableData(transformedData);
      setBarData(transformedBarData);
      shareTableData?.(transformedData);
    }
  }, [behaviorAnalysisIncidents]);

  const title = "Repeat Behavior Incidents";

  // ---------------------------------------------------------------------------
  // Handle clicking a bar in the chart
  // ---------------------------------------------------------------------------
  const handleBarClicked = (chart: any) => {
    if (!chart.length) return;
    const label = handlePointClicked(chart, barData?.labels);
    setShowModal(true);
    setResidentName(label);
    // Get drilldown data for the clicked resident from repeatIncidentsSection
    const drilldownData = behaviorAnalysisIncidents?.repeatIncidentsSection?.repeatIncidentsDrilldown || {};
    const filteredDetails = drilldownData[label] || [];
    setModalData(filteredDetails);
  };

  // ---------------------------------------------------------------------------
  // Build the grid component when tableData changes
  // ---------------------------------------------------------------------------
  useEffect(() => {
    const cellCountProps = {
      cells: { data: CenteredWholeNumCell, headerCell: HeaderCenteredCell },
    };
    const cellPercentageProps = {
      cells: {
        data: CenteredWholeNumCell,
        append: "%",
        headerCell: HeaderCenteredCell,
      },
    };

    const grid = (
      <Fragment>
        <Grid
          data={tableData || []}
          scrollable="none"
          rows={{ data: TotalRow }}
        >
          <GridColumn field="residentName" title="Resident" />
          <GridColumn
            field="count"
            title="Number"
            {...(cellCountProps as GridColumnProps)}
          />
          <GridColumn
            field="percentage"
            title="%"
            {...(cellPercentageProps as GridColumnProps)}
          />
        </Grid>
      </Fragment>
    );
    setGrid(grid);
  }, [tableData]);

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  return (
    <LayerContent>
      <Header>{title}</Header>
      <Container>
        <Child flex="40%">
          {grid}
        </Child>
        <Child flex="60%">
          <ChartContainer>
            {barData?.labels?.length > 0 ? (
              <Bar options={barOptionsHorizontal(handleBarClicked)} data={barData} />
            ) : (
              <NoData>No Data</NoData>
            )}
          </ChartContainer>
        </Child>
      </Container>

      {showModal && (
        <SectionModal
          open={showModal}
          facilityName={facilityLabel}
          startDate={startDate}
          endDate={endDate}
          closeFn={() => setShowModal(false)}
          resetFn={() => {
            setResidentName("");
            setModalData([]);
          }}
          isLoading={isFetchingSelectedIncident}
          tableData={modalData}
          tableCols={behaviorAnalysisDetailCols()}
          title={`${title} - ${residentName}`}
          getUserCallback={(item: any) => setClickedIncident(item)}
          handlePdfExport={() =>
            handlePDFDownload({
              data: modalData,
              columnData: behaviorAnalysisDetailCols(),
              fileName: facilityLabel,
              startDate,
              endDate,
              subTitle: `${title} - ${residentName}`,
            })
          }
          handleCsvExport={() =>
            handleCSVDownload(
              modalData,
              behaviorAnalysisDetailCols(),
              `${facilityLabel} - ${title} - ${residentName}`
            )
          }
        />
      )}
    </LayerContent>
  );
};

export default BehaviorRepeatIncidents;
